import { Suspense, useCallback, useMemo } from 'react'
import { Navigate, Route, Routes, useLocation } from 'react-router-dom'
import { LoadingLogo } from '@labrav/react-components'
import {
  CRMBlockPage,
  CRMSelector,
  CloudSettings,
  LayoutWrapper,
  LoadingTypes,
  OnboardingProductType as ProductType,
  SnackBar,
  TermsAndConditionsComponent,
  isLoading,
} from '@labrav/common'
import { isEmpty } from 'lodash'
import { customerFormData } from '../components/MarketplaceMappingManager/utils/customerFormData'
import { useSelector } from '../store'
import { FlyoutBlockPage } from '../common/components/FlyoutBlockPage'
import { FlyOutOnboardingAWSRequirements } from '../components/FlyOutOnboardingAWSRequirements'
import { FlyoutOnboardingWrapper } from '../components/FlyOutOnboardingWrapper'

const Loading = isLoading

export const GetFlyoutRoutesWhenNotOnboarded = () => {
  const location = useLocation()

  const queryParams = new URLSearchParams(location.search)
  const firstTimeValue = queryParams.get('newFirst')
  const flyOutOnboardingForm = useSelector(
    state => state.flyOutOnboarding.onboardingForm
  )

  const partnerData = useSelector(
    state => state.AccountData.user.partnerData || undefined
  )
  const subscribedProducts = partnerData?.subscribedProducts

  const flyoutProduct = subscribedProducts?.FLYOUT

  const isFlyoutSubscribed = !!flyoutProduct
  const flyoutCloudProviders = flyoutProduct?.cloudProviders || []

  const hasAWS = flyoutCloudProviders.includes('AWS')
  const hasAzure = flyoutCloudProviders.includes('AZURE')

  const isLoading =
    useSelector<boolean>(state =>
      Loading(state.loading)(LoadingTypes.PARTNER_DATA)
    ) ||
    isEmpty(partnerData) ||
    (hasAWS && isEmpty(flyOutOnboardingForm)) ||
    (hasAWS && isEmpty(flyOutOnboardingForm?.pageMarker))
  const isAllowedForFlyout = useMemo(() => {
    if (!isLoading && partnerData && isFlyoutSubscribed) {
      return true
    }
    return false
  }, [isLoading, partnerData, isFlyoutSubscribed, flyOutOnboardingForm])

  const { labraPartnerDetails, pageMarker } = flyOutOnboardingForm
  const isUserManaged = labraPartnerDetails?.isManaged

  const initialNavigateBasedOnForm = useCallback(() => {
    if (firstTimeValue) {
      if (flyoutCloudProviders[0] === 'AWS') {
        return `/flyout/onboarding/aws-requirements${
          firstTimeValue ? '?newFirst=true' : ''
        }`
      } else {
        return `/flyout/onboarding/company-information${firstTimeValue ? '?newFirst=true' : ''}`
      }
    }
    if (!pageMarker?.page1) {
      return '/flyout/onboarding/company-information'
    } else if (!pageMarker?.page2 && !isUserManaged) {
      return '/flyout/onboarding/cloud-settings/aws'
    } else {
      return '/flyout/onboarding/aws-requirements'
    }
  }, [
    flyOutOnboardingForm,
    isUserManaged,
    firstTimeValue,
    flyoutCloudProviders,
  ])

  const { crmType } = customerFormData()
  const isCrmLabra = crmType === 'labra'
  const showCrm = !isCrmLabra

  if (isLoading) {
    return <LoadingLogo />
  }

  const NewCompanyDetailsPage = () => (
    <TermsAndConditionsComponent productType={ProductType.FLYOUT} />
  )

  return (
    <Suspense fallback={<div></div>}>
      <Routes>
        <Route path="/settings/flyout/cloud/aws" element={<CloudSettings />} />
        {isAllowedForFlyout ? (
          <>
            <Route
              path="/onboarding/aws-requirements"
              element={
                <LayoutWrapper
                  title="SaaS Product Requirements to List on AWS Marketplace"
                  component={FlyOutOnboardingAWSRequirements}
                  isSidePanelOpened={true}
                />
              }
            />

            <Route
              path="onboarding/cloud-settings/*"
              element={
                hasAWS || hasAzure ? (
                  <LayoutWrapper
                    title="Marketplace Onboarding - Cloud settings"
                    component={FlyoutOnboardingWrapper}
                    isSidePanelOpened={true}
                    paddingZero={true}
                  />
                ) : (
                  <LayoutWrapper
                    paddingZero={true}
                    component={FlyoutBlockPage}
                    isSidePanelOpened={true}
                  />
                )
              }
            />

            <Route
              path="onboarding/company-information"
              element={
                <LayoutWrapper
                  component={NewCompanyDetailsPage}
                  isSidePanelOpened={true}
                  paddingZero={true}
                />
              }
            />

            <Route
              path="onboarding/crm-settings"
              element={
                showCrm ? (
                  <LayoutWrapper
                    title="CRM settings"
                    component={CRMSelector}
                    isSidePanelOpened={true}
                  />
                ) : (
                  <LayoutWrapper
                    paddingZero={true}
                    component={CRMBlockPage}
                    isSidePanelOpened={true}
                  />
                )
              }
            />
          </>
        ) : (
          <Route
            path="/onboarding/*"
            element={
              <LayoutWrapper
                paddingZero={true}
                component={FlyoutBlockPage}
                isSidePanelOpened={true}
              />
            }
          />
        )}

        <Route
          path="*"
          element={<Navigate to={initialNavigateBasedOnForm()} />}
        />
      </Routes>
      <SnackBar />
    </Suspense>
  )
}
